/**
 * Client-facing view of the shipping model.
 *
 * The tables and the pricing engine live in `shippingModel.js`, which the
 * server loads too — this module only adapts them to what listing UI needs:
 * "for this one book, what can the seller send it by, and what does each cost?"
 *
 * Prices here are estimates for display. The server recomputes them from the
 * same engine when a transaction is speculated, and that is what the buyer is
 * charged.
 */

import {
  DPD_KEY,
  YODEL_HOME_KEY,
  YODEL_STORE_KEY,
  INPOST_LOCKER_TO_HOME_KEY,
  INPOST_LOCKER_TO_LOCKER_KEY,
  SELF_SHIPPING_KEY,
  shippingOptionOrder,
  shipmentsForAdmin,
  isShippingOptionDisabled,
  basketShippingPrice,
  sellerPostagePrice,
  sellerPostageTotal,
} from './shippingModel';

export {
  DPD_KEY,
  YODEL_HOME_KEY,
  YODEL_STORE_KEY,
  INPOST_LOCKER_TO_HOME_KEY,
  INPOST_LOCKER_TO_LOCKER_KEY,
  SELF_SHIPPING_KEY,
  shippingOptionOrder,
  shipmentsForAdmin,
  isShippingOptionDisabled,
};

/**
 * Price of one delivery method for a single copy of one book.
 *
 * Self-shipping is priced by the seller rather than by a container tier, and may
 * legitimately be 0 (a local swap or handover).
 *
 * @param {string} option - delivery method key
 * @param {Object} book - `{ size, format }` from the listing's public data
 * @param {number|string} selfShippingPrice - the seller's own price
 * @returns {number|null} price in major units, or null if not purchasable
 */
const shippingOptionPrice = (option, book, selfShippingPrice) => {
  if (option === SELF_SHIPPING_KEY) {
    return sellerPostagePrice(selfShippingPrice);
  }
  return basketShippingPrice(option, [book]);
};

/**
 * The delivery methods actually purchasable for a listing: the seller's own
 * preferences, minus anything switched off, minus anything with no usable price.
 *
 * Sorted cheapest first so callers can take [0] as the headline option.
 *
 * @param {Object} params
 * @param {string[]} params.shippingPreferences - the seller's supported methods
 * @param {string} params.bookSize - listing publicData.parcelSize. The key is
 *   legacy; the value describes the book, not a parcel (see shippingModel).
 * @param {string} params.bookFormat - listing publicData.book_type
 * @param {number|string} params.selfShippingPrice
 * @returns {Array<{key: string, price: number, isSelfShipping: boolean, isFree: boolean}>}
 */
export const getAvailableShippingOptions = params => {
  const { shippingPreferences = [], bookSize, bookFormat, selfShippingPrice } = params;
  const book = { size: bookSize, format: bookFormat };

  return shippingOptionOrder
    .filter(option => shippingPreferences.includes(option) && !isShippingOptionDisabled(option))
    .map(option => {
      const price = shippingOptionPrice(option, book, selfShippingPrice);
      return price == null
        ? null
        : {
            key: option,
            price,
            isSelfShipping: option === SELF_SHIPPING_KEY,
            isFree: price === 0,
          };
    })
    .filter(Boolean)
    .sort((a, b) => a.price - b.price);
};

// A cart entry describes a listing and how many copies are in the basket; the
// engine works in individual books, so entries expand on the way in.
const booksInto = (books, item) =>
  books.concat(
    Array.from({ length: item.count }, () => ({ size: item.bookSize, format: item.bookFormat }))
  );


/**
 * The delivery figure to advertise on a listing, and whether a free option
 * exists alongside it.
 *
 * The headline is the cheapest *paid* option, not the cheapest option outright:
 * "from free" would anchor the buyer to a local handover most of them cannot
 * use. A free option is mentioned separately instead.
 *
 * This is a floor, and deliberately says nothing about what is already in the
 * buyer's cart. Postage is priced per basket, so the only figure a single
 * listing can honestly quote is the one for that book travelling alone — see
 * `getBundleSaving` for the real number, which belongs in the cart.
 *
 * @returns {{fromPrice: number|null, hasFreeOption: boolean, hasAnyOption: boolean}}
 */
export const getDeliveryFloor = params => {
  const options = getAvailableShippingOptions(params);
  const cheapestPaid = options.find(o => !o.isFree);
  return {
    fromPrice: cheapestPaid ? cheapestPaid.price : null,
    hasFreeOption: options.some(o => o.isFree),
    hasAnyOption: options.length > 0,
  };
};

/**
 * What one delivery method costs for a whole basket.
 *
 * The same engine checkout prices with, so the figure shown beside each option
 * is the figure that will be charged for it. Returns null when the method
 * cannot carry the basket, rather than a misleading number.
 *
 * @param {Object} params
 * @param {string} params.deliveryMethod
 * @param {Array<{bookSize: string, bookFormat: string, count: number,
 *   selfShippingPrice: number|string}>} params.items - one entry per listing
 * @returns {number|null} price in major currency units
 */
export const getCartShippingPrice = params => {
  const { deliveryMethod, items = [] } = params;

  if (!deliveryMethod || items.length === 0) {
    return null;
  }

  // Self-shipping is not packed into containers: the seller names a price per
  // book and every book in the basket is charged it (§A8). It is only on offer
  // when every book in the basket has a price: a book without one has no
  // postage the seller agreed to, so the method is unavailable, not free.
  if (deliveryMethod === SELF_SHIPPING_KEY) {
    return sellerPostageTotal(
      items.map(item => ({ price: item.selfShippingPrice, count: item.count }))
    );
  }

  try {
    return basketShippingPrice(deliveryMethod, items.reduce(booksInto, []));
  } catch (e) {
    // The engine refuses baskets it cannot carry. There is no price to show for
    // an order that cannot be placed; checkout reports why.
    return null;
  }
};

/**
 * The delivery methods a seller's basket can actually be sent with, each with its
 * price for this basket, in display order.
 *
 * A method the seller offers is left out when it cannot be priced for this
 * basket: most often "Seller arranges postage" when a book has no seller price.
 * Offering it anyway would either quote a postage the seller never agreed to or
 * fail at checkout. The cart page, the drawer and the checkout check all use
 * this, so they agree.
 *
 * @param {Object} params
 * @param {Array<string>} params.sellerShippingPreferences
 * @param {Array} params.items - one entry per listing, see getCartShippingPrice
 * @returns {Array<{key: string, price: number}>}
 */
export const getCartDeliveryOptions = params => {
  const { sellerShippingPreferences = [], items = [] } = params;
  return shippingOptionOrder
    .filter(key => sellerShippingPreferences.includes(key) && !isShippingOptionDisabled(key))
    .map(key => ({ key, price: getCartShippingPrice({ deliveryMethod: key, items }) }))
    .filter(option => option.price != null);
};

/**
 * What bundling actually saved, for one seller's cart.
 *
 * The counterfactual is one order per listing, not one per book: a cart holding
 * two copies of the same listing would have been a single order anyway, so
 * counting each copy separately would overstate the saving.
 *
 * Unlike the listing-page floor this is exact, because the cart is the one place
 * both unknowns are resolved — every book in the basket is known, and the buyer
 * has picked a method. Returns null whenever there is nothing honest to claim:
 * a single listing, no method chosen, or a basket the engine will not price.
 *
 * @param {Object} params
 * @param {string} params.deliveryMethod - the method the buyer selected
 * @param {Array} params.items - as `getCartShippingPrice`
 * @returns {{together: number, separate: number, saving: number}|null}
 */
export const getBundleSaving = params => {
  const { deliveryMethod, items = [] } = params;

  if (items.length < 2) {
    return null;
  }

  const together = getCartShippingPrice({ deliveryMethod, items });
  const perListing = items.map(item => getCartShippingPrice({ deliveryMethod, items: [item] }));

  if (together == null || perListing.some(price => price == null)) {
    return null;
  }

  const separate = perListing.reduce((sum, price) => sum + price, 0);
  const saving = Math.round((separate - together) * 100) / 100;
  return saving > 0 ? { together, separate, saving } : null;
};
